import { Controller } from "@hotwired/stimulus"

// Generic dropdown controller for user menu, help menu, etc.
//
// Usage:
//   <div data-controller="dropdown">
//     <button data-action="dropdown#toggle">Open</button>
//     <div data-dropdown-target="menu" class="hidden">
//       <!-- menu content -->
//     </div>
//   </div>

export default class extends Controller {
  static targets = ["menu"]

  connect() {
    this._onClickOutside = this._onClickOutside.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this._onReposition = this._onReposition.bind(this)
    this._onModalOpen = this._close.bind(this)
    this._onDropdownClose = (e) => { if (e.detail?.except !== this) this._close() }
    document.addEventListener("modal:open", this._onModalOpen)
    document.addEventListener("dropdown:close", this._onDropdownClose)
  }

  toggle(event) {
    event.stopPropagation()
    if (this.menuTarget.classList.contains("hidden")) {
      this._open()
    } else {
      this._close()
    }
  }

  _open() {
    document.dispatchEvent(new CustomEvent("dropdown:close", { detail: { except: this } }))
    this.menuTarget.classList.remove("hidden")
    this._positionMenu()
    document.addEventListener("click", this._onClickOutside)
    document.addEventListener("keydown", this._onKeydown)
  }

  _close() {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.add("hidden")
    this.menuTarget.style.bottom = ""
    this.menuTarget.style.top = ""
    this.menuTarget.style.left = ""
    this.menuTarget.style.right = ""
    this.menuTarget.style.position = ""
    this.menuTarget.style.width = ""
    if (this._scrollListeners) {
      this._scrollListeners.forEach(({ node, fn }) => node.removeEventListener("scroll", fn))
      window.removeEventListener("resize", this._onReposition)
      this._scrollListeners = null
    }
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onKeydown)
  }

  _positionMenu() {
    const menu = this.menuTarget
    const scrollParent = this._scrollParent(this.element)

    if (scrollParent) {
      // Inside a scroll container: use position:fixed so the menu can
      // escape any clipping ancestor. Re-position on scroll/resize.
      this._fixedPosition()
      this._scrollListeners = []
      let node = this.element.parentElement
      while (node) {
        node.addEventListener("scroll", this._onReposition, { passive: true })
        this._scrollListeners.push({ node, fn: this._onReposition })
        node = node.parentElement
      }
      window.addEventListener("resize", this._onReposition)
      return
    }

    const rect = menu.getBoundingClientRect()
    if (rect.bottom > window.innerHeight) {
      const triggerRect = this.element.getBoundingClientRect()
      if (triggerRect.top > rect.height) {
        menu.style.top = "auto"
        menu.style.bottom = "100%"
      }
    }
  }

  _fixedPosition() {
    const menu = this.menuTarget
    const trigger = this.element.getBoundingClientRect()
    menu.style.position = "fixed"
    menu.style.top = "auto"
    menu.style.bottom = "auto"
    menu.style.left = "auto"
    menu.style.right = "auto"
    // Render once to measure
    menu.style.visibility = "hidden"
    menu.style.top = `${trigger.bottom + 4}px`
    menu.style.left = `${trigger.right - menu.offsetWidth}px`
    const menuRect = menu.getBoundingClientRect()
    // Flip up if we'd overflow the viewport bottom
    if (menuRect.bottom > window.innerHeight && trigger.top > menuRect.height) {
      menu.style.top = `${trigger.top - menuRect.height - 4}px`
    }
    menu.style.visibility = ""
  }

  _onReposition() {
    if (this.menuTarget.classList.contains("hidden")) return
    this._fixedPosition()
  }

  _scrollParent(el) {
    let node = el.parentElement
    while (node) {
      const overflow = getComputedStyle(node).overflowY || getComputedStyle(node).overflow
      if (overflow === "auto" || overflow === "scroll" || overflow === "hidden") return node
      node = node.parentElement
    }
    return null
  }

  _onClickOutside(event) {
    // Use composedPath() to capture ancestry at dispatch time. Action
    // handlers that replace innerHTML inside the menu detach the click
    // target before this listener runs, so this.element.contains(event.target)
    // returns false even though the click originated inside the dropdown.
    const path = (typeof event.composedPath === "function") ? event.composedPath() : []
    if (this.element.contains(event.target) || path.includes(this.element)) return
    this._close()
  }

  _onKeydown(event) {
    if (event.key === "Escape") {
      this._close()
    }
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("modal:open", this._onModalOpen)
    document.removeEventListener("dropdown:close", this._onDropdownClose)
  }
}
